import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { InsuranceProviderManagement } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import { InsuranceProvider } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/InsuranceProviders/PathItems';
import EditInsuranceProvider from './EditInsuranceProvider';
import AISuggestions from '../../components/AISuggestions';

function InsuranceProviders() {
  const { onError } = useToast();
  const [search, setSearch] = useState('');
  const [filteredProviders, setFilteredProviders] = useState<InsuranceProvider[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newProviderName, setNewProviderName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<{ id: string, name: string } | null>(null);
  const [editingProvider, setEditingProvider] = useState<string | null>(null);

  const searchProviders = async (query: string) => {
    onError((await Session.getClient().request(InsuranceProviderManagement.searchInsuranceProvider({ query, limit: 100, offset: 0 })))
      .maybeOk((value) => setFilteredProviders(value as InsuranceProvider[])));
  };

  useEffect(() => {
    searchProviders('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddProvider = () => {
    setNewProviderName('');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewProviderName('');
  };

  const handleDialogOk = async () => {
    if (newProviderName.trim()) {
      onError((await Session.getClient().request(InsuranceProviderManagement.createInsuranceProvider({ name: newProviderName.trim() })))
        .maybeOk(() => searchProviders(search)));
    }
    handleDialogClose();
  };

  const handleDeleteClick = (id: string, name: string) => {
    setProviderToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setProviderToDelete(null);
  };

  const handleDeleteDialogOk = async () => {
    if (providerToDelete) {
      onError((await Session.getClient().addPathParam('insuranceProviderId', providerToDelete.id).request(InsuranceProviderManagement.deleteInsuranceProvider()))
        .maybeOk(() => searchProviders(search)));
    }
    handleDeleteDialogClose();
  };

  if (editingProvider) {
    return <EditInsuranceProvider id={editingProvider} onBack={() => {
      searchProviders(search);
      setEditingProvider(null);
    }} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <TextField
          label="Search insurance providers"
          size="small"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            searchProviders(e.target.value);
          }}
          sx={{ width: 250 }}
        />
        <AISuggestions 
          client={Session.getClient()}
          currentSnapshot={JSON.stringify(filteredProviders)}
          usageInstructions={InsuranceProviderManagement.toAgentInstructions()}
          developerMessage={'The user is in the view where they can search and see a list of insurance providers. For any of the operations ' +
            'regarding a specific provider the path parameter with the name insuranceProviderId MUST be specified with that provider id.'}
          onOperationsApplied={() => searchProviders(search)}
        />
        <Button variant="contained" color="primary" sx={{width: 350}} onClick={handleAddProvider}>Create Insurance Provider</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><Typography fontWeight="bold">Name</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Published</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight="bold">Actions</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProviders.map((prov) => (
              <TableRow key={prov.id}>
                <TableCell>{prov.name}</TableCell>
                <TableCell>
                  <Switch checked={prov.state.state === 'published'} disabled />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="primary" onClick={() => setEditingProvider(prov.id)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteClick(prov.id, prov.name)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Create Insurance Provider</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Provider Name"
            fullWidth
            autoFocus
            value={newProviderName}
            onChange={e => setNewProviderName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogOk} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>Delete Insurance Provider</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the insurance provider "{providerToDelete?.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteDialogOk} variant="contained" color="error">OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InsuranceProviders;
